import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ImageWithFallback from '../imageWithFalback';
import { useNavigate } from 'react-router-dom';

interface CollectionItem {
    id: string | number;
    name: string;
    description: string;
    image: string;
    serialNumber?: string;
}

interface CollectionProps {
    items: CollectionItem[];
    title?: string;
    className?: string;
    collectionId?: string;
    clickable?: boolean;
}

function Collection({ items, title, className = '', collectionId, clickable = true }: CollectionProps) {
    const navigate = useNavigate();
    const [emptyBoxesNeeded, setEmptyBoxesNeeded] = useState(0);
    const columnsPerRow = 3;

    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const staggerChildren = {
        animate: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    useEffect(() => {
        const totalItems = items.length;
        const itemsInLastRow = totalItems % columnsPerRow;
        setEmptyBoxesNeeded(itemsInLastRow === 0 ? 0 : columnsPerRow - itemsInLastRow);
    }, [items]);

    const handleItemClick = (item: CollectionItem) => {
        console.log("item", item);
        if (clickable) {
            navigate(`/collections/${collectionId}/${item.id}`);
        }
    };

    return (
        <section className={`w-full ${className}`}>
            {title && (
                <motion.h2
                    className="text-2xl md:text-3xl font-light mb-12 text-center"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                >
                    {title}
                </motion.h2>
            )}
            <motion.div
                variants={staggerChildren}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                key={items.length}
                className="border-t border-l border-[#747473FF]"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {items.map((item, index) => (
                        <motion.div
                            key={item.id}
                            onClick={() => handleItemClick(item)}
                            className={`${clickable ? 'cursor-pointer' : 'cursor-default'} group p-8 md:p-12 
                                border-r ${index === items.length - 1 ? 'md:border-b' : 'border-b'} border-[#747473FF] transition-colors duration-300`}
                            variants={fadeIn}
                        >
                            <div className="relative aspect-square mb-8 overflow-hidden">
                                <ImageWithFallback
                                    src={item.image}
                                    alt={item.name}
                                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                                />
                            </div>
                            <div className="text-center">
                                <h3 className="text-sm uppercase tracking-wider mb-2">{item.name}</h3>
                                <p className="text-xs text-gray-800 font-light">{item.description}</p>
                            </div>
                        </motion.div>
                    ))}
                    {/* Add empty boxes to complete the last row - only visible on md and lg screens */}
                    {Array.from({ length: emptyBoxesNeeded }).map((_, index) => (
                        <motion.div
                            key={`empty-${index}`}
                            className="hidden md:block p-8 md:p-12 border-r border-b border-[#747473FF]"
                            variants={fadeIn}
                        >
                            <div className="relative aspect-square mb-8 overflow-hidden" />
                            <div className="text-center">
                                <div className="h-6 mb-2" />
                                <div className="h-4" />
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
}

export default Collection; 